import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { socketUseEffect } from "../../socket";
import BasicCRUD from './../core/basic-crud';
import BadgeStatusView from "./sub-pages/elements/BadgeStatusView";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";

// ---------- i18n ----------
const plural = (n, { one, few, many }) => {
    const n10 = n % 10, n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return one;
    if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
    return many;
};

const dict = {
    en: {
        // page
        title: "Load Speed",
        subTitle: "add / edit load speed tests",
        homeURL: "/dashboard",
        addTitle: "Add Load Speed Test",
        editTitle: "Edit Load Speed Test",

        // columns
        colStatus: "Status",
        colDomainName: "Domain Name",
        colContactGroup: "Contact Group",

        // cells
        unknownDomain: "Unknown Domain",
        invalidDate: "Invalid Date",
        expiration: "Expiration:",
        expired: "Expired",
        notAvailable: " - ",
        dayLbl: (n) => `day${n === 1 ? "" : "s"}`,
        hourLbl: (n) => `hour${n === 1 ? "" : "s"}`,

        // form
        tabMain: "Main",
        fieldDomainName: "Domain Name",
        fieldContactGroup: "Contact Group",
        choose: " -- choose -- ",

        // actions
        edit: "Edit",
        delete: "Delete",

        // quotas
        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",

        // delete dialog
        delAlert: "Deleting a load speed test cannot be undone.",
        delText: "To confirm deletion, enter the name of the load speed test in the next input field",
        delTitle: 'Delete load speed test: "{name}"',

        // toasts
        toastEntity: "Load Speed Test",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
    },

    ru: {
        title: "Скорость загрузки",
        subTitle: "добавление / редактирование тестов скорости",
        homeURL: "/dashboard",
        addTitle: "Добавить тест скорости",
        editTitle: "Редактировать тест скорости",

        colStatus: "Статус",
        colDomainName: "Имя домена",
        colContactGroup: "Группа контактов",

        unknownDomain: "Неизвестный домен",
        invalidDate: "Неверная дата",
        expiration: "Истекает:",
        expired: "Истёк",
        notAvailable: " - ",
        dayLbl: (n) => plural(n, { one: "день", few: "дня", many: "дней" }),
        hourLbl: (n) => plural(n, { one: "час", few: "часа", many: "часов" }),

        tabMain: "Основное",
        fieldDomainName: "Имя домена",
        fieldContactGroup: "Группа контактов",
        choose: " -- выберите -- ",

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",

        delAlert: "Удаление теста скорости необратимо.",
        delText: "Чтобы подтвердить удаление, введите имя теста скорости в следующее поле ввода",
        delTitle: 'Удалить тест скорости: «{name}»',

        toastEntity: "Тест скорости",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",
    },

    ua: {
        title: "Швидкість завантаження",
        subTitle: "додавання / редагування тестів швидкості",
        homeURL: "/dashboard",
        addTitle: "Додати тест швидкості",
        editTitle: "Редагувати тест швидкості",

        colStatus: "Статус",
        colDomainName: "Назва домену",
        colContactGroup: "Група контактів",

        unknownDomain: "Невідомий домен",
        invalidDate: "Невірна дата",
        expiration: "Завершення:",
        expired: "Строк минув",
        notAvailable: " - ",
        dayLbl: (n) => plural(n, { one: "день", few: "дні", many: "днів" }),
        hourLbl: (n) => plural(n, { one: "година", few: "години", many: "годин" }),

        tabMain: "Основне",
        fieldDomainName: "Назва домену",
        fieldContactGroup: "Група контактів",
        choose: " -- оберіть -- ",

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",

        delAlert: "Видалення тесту швидкості неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву тесту швидкості у наступне поле вводу",
        delTitle: 'Видалити тест швидкості: «{name}»',

        toastEntity: "Тест швидкості",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- component ----------
const LoadSpeed = ({ lang: langProp, ...props }) => {
    const { user } = useAuth();
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "loadspeed";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [totalData, setTotalData] = useState([]);
    const [contacts, setContacts] = useState([{ id: 0, name: t.choose }]);

    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];
    useEffect(() => socketUseEffect(listeners, user), [response?.status, response?.created, response?.updated, response?.delete]); // eslint-disable-line

    const formatStatus = (data) => <BadgeStatusView status={data.status} lang={lang} />;

    const formatDomainDetails = (data) => {
        const domainName = data.domain_name || t.unknownDomain;

        // expiration date
        const exp = new Date(data.date_expiration);
        const formattedExpirationDate = isNaN(exp)
            ? t.invalidDate
            : exp.toLocaleDateString('en-GB', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }).replace(',', '');

        // days left
        const now = moment();
        const expiration = moment(data.date_expiration);
        const diff = moment.duration(expiration.diff(now));

        let color = '#f20000';
        let daysLeftText = t.expired;

        if (diff.asMilliseconds() > 0) {
            const days = Math.floor(diff.asDays());
            const hours = diff.hours();

            if (days <= 1) color = '#f20000';
            else if (days <= 10) color = '#ff9a44';
            else color = '#4CAF50';

            const dLbl = t.dayLbl(days);
            const hLbl = t.hourLbl(hours);
            daysLeftText = `${days} ${dLbl}${hours > 0 ? ` ${hours} ${hLbl}` : ''}`;
        }

        const styles = {
            container: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
            domainName: { fontWeight: 'bold', fontSize: 14, marginBottom: 4 },
            expirationAndDaysLeft: { fontSize: 12, color: '#333' },
            daysLeft: { color, fontWeight: 'bold' },
        };

        return (
            <div style={styles.container}>
                <span style={styles.domainName}>{domainName}</span>
                <span style={styles.expirationAndDaysLeft}>
          {t.expiration} {String(data.status) === "0"
                    ? (<>{t.notAvailable}</>)
                    : (<>{formattedExpirationDate} — <span style={styles.daysLeft}>{daysLeftText}</span></>)}
        </span>
            </div>
        );
    };

    const columns = [
        { label: t.colStatus, key: "status", format: formatStatus },
        { label: t.colDomainName, key: "domain_name", format: formatDomainDetails },
        { label: t.colContactGroup, key: "contacts_group_name" },
    ];

    const fields = {
        add: [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [{ label: t.fieldDomainName, key: "domain_name", default_value: "", showLabel: "top", readonly: false }],
                    [{ label: t.fieldContactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" }],
                ],
            }
        ],
        edit: [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [{ label: t.fieldDomainName, key: "domain_name", default_value: "", showLabel: "top", readonly: true }],
                    [{ label: t.fieldContactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" }],
                ],
            }
        ],
    };

    const actions = [
        { name: "edit", label: t.edit, controller: "crud" },
        { name: "delete", label: t.delete, controller: "crud" },
    ];

    const expired = user?.expired ?? 1;
    const disabled = expired
        ? { add: { disable: true, title: t.planExpired } }
        : { add: { disable: (totalData.loadspeed - totalData.used_loadspeed) < 1, title: t.noResources } };

    return (
        <BasicCRUD
            singleArgs={props?.singleArgs ?? false}
            title={t.title}
            subTitle={t.subTitle}
            homeURL={t.homeURL}
            addTitle={t.addTitle}
            disable={disabled}
            editTitle={t.editTitle}
            object={object}
            columns={columns}
            fields={fields}
            actions={actions}
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            response={response}
            setResponse={setResponse}
            ownHandler={true}
            deleteConfirmationDialog={{
                alert: t.delAlert,
                text: t.delText,
                title: t.delTitle,
                column: 'name',
            }}
        >
            <ProgressBar lang={lang} used={totalData.used_loadspeed} allowed={totalData.loadspeed} />
            {response.status === 1 ? (
                <FadeAlert response={response} resetResponse={() => setResponse([])}>
                    {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                    {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                    {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                </FadeAlert>
            ) : null}
        </BasicCRUD>
    );
};

export default LoadSpeed;
